'use client';

import React, { useState } from 'react';

import { Heading, Caption } from '@depop/web-thrift-ui/Typography';

import styles from './styles.module.css';

import { Carousel, type NavigationState } from '@/components/Carousel';
import { NavButtons } from '@/components/Carousel/NavButtons';
import { ExtendedLink } from '@/components/ExtendedLink';
import { ModuleHeader, ModuleHeaderSize } from '@/components/ModuleHeader';
import { ScrollableItemRow } from '@/components/ScrollableItemRow';
import {
  ActivityTrackerEventType,
  HomePageModuleNames,
} from '@/modules/activityTracker/constants.ts';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker.ts';
import { type PopularThisWeekData } from '@/modules/cms/helpers/assertions/popularThisWeek.ts';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { createCMSTileHref, getCMSImageForVariant } from '@/modules/home/utils';

type Props = {
  data: PopularThisWeekData;
};

type Item = {
  subtitle: string;
  title: string;
  link: string;
  image: {
    url: string;
    width: number;
    height: number;
  };
};

const ITEMS_PER_SLIDE = {
  xs: 2,
  sm: 2,
  md: 4,
  lg: 4,
  xl: 4,
};

function getProductToDisplay(
  item: Item,
  index: number,
  sendATEvent: (index: number, title: string) => void,
  experimentVariant: string | undefined
) {
  const { title, subtitle } = item;
  const [amount, ...restOfSalesInfo] = subtitle.split(' ');
  const searchesText = restOfSalesInfo.join(' ');

  if (!title) {
    return null;
  }

  const image = getCMSImageForVariant(item, experimentVariant);

  return (
    <ExtendedLink
      className={styles.link}
      key={`${item.title?.slice(0, 4)}_${index}`}
      href={createCMSTileHref(item, HomePageModuleNames.PopularThisWeek)}
      onClick={() => {
        sendATEvent(index, title);
      }}
    >
      <div className={styles.imgContainer}>
        <img
          src={`${image?.url}?w=500&fm=webp`}
          width={image?.width}
          height={image?.height}
          alt=""
        />
      </div>
      <div className={styles.textContainer}>
        <Heading variant="sm" as="p">
          {title}
        </Heading>
        <Caption variant="lg-regular" className={styles.text}>
          <b>{amount} </b>
          {searchesText}
        </Caption>
      </div>
    </ExtendedLink>
  );
}

export function PopularThisWeekUi({ data }: Props) {
  const { sendActivityTrackerEvent } = useActivityTracker();
  const { web_7707_broaden_appeal_imagery } = useExperiments([
    'web_7707_broaden_appeal_imagery',
  ]);
  const [navigationState, setNavigationState] =
    useState<NavigationState | null>(null);

  function sendATEvent(itemPosition: number, title: string) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: HomePageModuleNames.PopularThisWeek,
      itemClicked: title,
      itemPosition,
      schemaVersion: '3.0',
    });
  }

  const carouselItems = data.items.map((item, index) =>
    getProductToDisplay(
      item,
      index,
      sendATEvent,
      web_7707_broaden_appeal_imagery
    )
  );

  return (
    <section>
      <div className={styles.headerRow}>
        <ModuleHeader
          title={data.titleCopy}
          size={ModuleHeaderSize.Large}
          id="popular-this-week-heading"
        />

        {navigationState && (
          <NavButtons hideOnMobile navigationState={navigationState} />
        )}
      </div>

      <div className={styles.scrollableRowContainer}>
        <ScrollableItemRow
          itemsPerRow={carouselItems.length}
          minItemWidth={256}
          itemGap={8}
        >
          {carouselItems}
        </ScrollableItemRow>
      </div>
      <div className={styles.carouselContainer}>
        <Carousel
          id="popularThisWeek__carousel"
          aria-labelledby="popular-this-week-heading"
          items={carouselItems}
          itemsPerSlide={ITEMS_PER_SLIDE}
          onNavigationStateChange={setNavigationState}
          itemGap={16}
        />
      </div>
    </section>
  );
}
